import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import {
  useResolutionsList,
  useRun,
  type AgentRun,
  type Counterparty,
  type MissingDocument,
  type OpenVariable,
  type Resolution,
} from "../api";

import { OpenVariableCard } from "../components/OpenVariableCard";
import { RouteBadge } from "../components/RouteBadge";
import { VerdictPill } from "../components/VerdictPill";
import { formatRelative } from "../utils/formatRelative";

import styles from "./RunDetailRoute.module.css";

// Port of the legacy ShowcaseTimeline + RunSummaryCard surface, but as a
// standalone deep-linkable page rather than the post-submit
// after-state of the New Project form.
//
// Route: /runs/:projectId/:runId
//
// The page fetches one run by (project_id, run_id) and renders four
// stacked sections:
//   1. Header - project + run identifiers, when it ran, route + verdict
//   2. Summary - "what this run did" (Analysed / Decided / Outstanding)
//   3. Decision - action + rationale + citations
//   4. Project state - open variables, missing documents, counterparties
//
// When the "Actionable next actions" feature lands (scoped doc), the
// Open variables card becomes interactive. Until then it is read-only.

const approvalRe = /(approv|authoris|finance|nik|john)/i;

// Surface AgentDecision.known_gaps as a prominent amber banner at
// the top of Run Detail. Each entry is a short string the agent
// emitted when it KNEW it had read only part of a document (the
// extractor's truncation envelope obliges Engineer / Grid to
// populate this list). The banner is the operator's main visible
// signal that the agent's reasoning is calibrated against
// incomplete inputs - re-upload split / extract pages and re-run.
const KnownGapsBanner = ({ gaps }: { gaps: string[] }) => {
  if (gaps.length === 0) return null;
  return (
    <section className={styles.gapsBanner}>
      <div className={styles.gapsHead}>
        Atlas didn't read everything ({gaps.length} document
        {gaps.length === 1 ? "" : "s"})
      </div>
      <p className={styles.gapsLede}>
        The agent flagged the following inputs as incompletely read.
        Re-upload split into smaller files, or extract specific page
        ranges separately, then re-run to close these gaps.
      </p>
      <ul className={styles.gapsList}>
        {gaps.map((g, i) => (
          <li key={i} className={styles.gapsItem}>
            {g}
          </li>
        ))}
      </ul>
    </section>
  );
};

const SummaryCard = ({ run }: { run: AgentRun }) => {
  const ps = run.decision?.project_state;
  const sourceCount = ps?.source_upload_ids?.length ?? 0;
  const imageCount = (run.file_refs ?? []).filter(
    (f) => f.kind === "site_photo",
  ).length;
  const counterpartyCount = ps?.counterparties?.length ?? 0;
  const mpanCount = ps?.mpans?.length ?? 0;
  const dateCount = ps?.contractual_dates?.length ?? 0;
  const openVars = ps?.open_variables ?? [];
  const missing = ps?.missing_documents ?? [];
  const costStatus = ps?.cost_commit_status ?? "unknown";
  const approvalCount = openVars.filter(
    (v) =>
      approvalRe.test(v.blocking_action ?? "") || approvalRe.test(v.name ?? ""),
  ).length;
  const agent = ps?.last_updated_by ?? "—";

  const analysedBits = [
    `${sourceCount} document${sourceCount === 1 ? "" : "s"}`,
    imageCount > 0 && `${imageCount} image${imageCount === 1 ? "" : "s"} via vision`,
    counterpartyCount > 0 &&
      `${counterpartyCount} counterpart${counterpartyCount === 1 ? "y" : "ies"}`,
    mpanCount > 0 && `${mpanCount} MPAN${mpanCount === 1 ? "" : "s"}`,
    dateCount > 0 &&
      `${dateCount} contractual date${dateCount === 1 ? "" : "s"}`,
  ].filter(Boolean) as string[];

  return (
    <section className={styles.summarySection}>
      <div className={styles.eyebrow}>What this run did</div>
      <div className={styles.summaryGrid}>
        <div className={styles.col}>
          <div className={styles.colLabel}>Analysed</div>
          <ul className={styles.list}>
            {analysedBits.length === 0 && (
              <li className={styles.muted}>No source artefacts in scope.</li>
            )}
            {analysedBits.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
        </div>
        <div className={styles.col}>
          <div className={styles.colLabel}>Decided</div>
          <ul className={styles.list}>
            <li>
              Routed to <strong>{agent}</strong>
              {run.triage?.grid_score != null &&
              run.triage?.predev_score != null
                ? ` (${run.triage.grid_score}-${run.triage.predev_score})`
                : null}
            </li>
            <li>
              Verdict <VerdictPill verdict={run.decision?.verdict} />
              {typeof run.decision?.confidence === "number" && (
                <span className={styles.muted}>
                  {" · "}
                  {(run.decision.confidence * 100).toFixed(0)}% confidence
                </span>
              )}
            </li>
            <li>
              Cost-commit gate: <strong>{costStatus.replace(/_/g, " ")}</strong>
            </li>
          </ul>
        </div>
        <div className={styles.col}>
          <div className={styles.colLabel}>Outstanding</div>
          <ul className={styles.list}>
            {openVars.length === 0 && missing.length === 0 && approvalCount === 0 && (
              <li className={styles.muted}>
                Nothing blocking. Advance to the next stage.
              </li>
            )}
            {openVars.length > 0 && (
              <li>
                {openVars.length} open variable
                {openVars.length === 1 ? "" : "s"} to close
              </li>
            )}
            {missing.length > 0 && (
              <li>
                {missing.length} missing document
                {missing.length === 1 ? "" : "s"} to upload
              </li>
            )}
            {approvalCount > 0 && (
              <li>
                {approvalCount} item{approvalCount === 1 ? "" : "s"} need
                Nik/John approval
              </li>
            )}
          </ul>
        </div>
      </div>
    </section>
  );
};

const DecisionSection = ({ run }: { run: AgentRun }) => {
  const action = run.decision?.action;
  const rationale = run.decision?.rationale;
  const citations = run.decision?.citations ?? [];
  return (
    <section className={styles.card}>
      <h3 className={styles.h3}>Decision</h3>
      {action && (
        <div className={styles.field}>
          <div className={styles.fieldLabel}>Action</div>
          <p className={styles.fieldBody}>{action}</p>
        </div>
      )}
      {rationale && (
        <div className={styles.field}>
          <div className={styles.fieldLabel}>Rationale</div>
          <p className={styles.fieldBody}>{rationale}</p>
        </div>
      )}
      {citations.length > 0 && (
        <div className={styles.field}>
          <div className={styles.fieldLabel}>
            Citations ({citations.length})
          </div>
          <ul className={styles.citationsList}>
            {citations.map((c, i) => (
              <li key={i} className={styles.citationItem}>
                {c}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

// Mirrors backend ``resolutions._normalise``: lowercase, split on
// non-word chars, rejoin with single space, strip. The backend uses
// Python's ``re.compile(r"\W+")`` which is Unicode-aware - accented
// letters like ``é`` count as word chars. JavaScript's ``\W`` is
// ASCII-only by default, so we use a Unicode property class to
// match Python's behaviour exactly. Without the ``u`` flag + the
// ``\p{L}\p{N}_`` class, ``"café"`` would shred to ``"caf"`` on the
// FE while the backend keeps it intact - resolved-state badges
// would silently fail for any non-ASCII variable text.
const _normalise = (text: string): string =>
  text.toLowerCase().split(/[^\p{L}\p{N}_]+/u).filter(Boolean).join(" ");

const _variableMatchKey = (name: string, description: string): string =>
  `${_normalise(name)}||${_normalise(description)}`;

const OpenVariablesCard = ({
  projectId,
  sourceRunId,
  vars,
  resolutions,
}: {
  projectId: string;
  sourceRunId: string;
  vars: OpenVariable[];
  resolutions: Resolution[];
}) => {
  const resolutionByVariable = useMemo(() => {
    const m = new Map<string, Resolution>();
    for (const r of resolutions) {
      m.set(_variableMatchKey(r.variable_name, r.variable_description), r);
    }
    return m;
  }, [resolutions]);

  if (vars.length === 0) return null;
  return (
    <section className={styles.card}>
      <h3 className={styles.h3}>
        Open variables <span className={styles.count}>({vars.length})</span>
      </h3>
      <ul className={styles.varList}>
        {vars.map((v) => {
          const key = _variableMatchKey(v.name, v.description);
          return (
            <OpenVariableCard
              key={key}
              projectId={projectId}
              sourceRunId={sourceRunId}
              variable={v}
              resolution={resolutionByVariable.get(key)}
            />
          );
        })}
      </ul>
    </section>
  );
};

const MissingDocumentsCard = ({ docs }: { docs: MissingDocument[] }) => {
  if (docs.length === 0) return null;
  return (
    <section className={styles.card}>
      <h3 className={styles.h3}>
        Missing documents <span className={styles.count}>({docs.length})</span>
      </h3>
      <ul className={styles.varList}>
        {docs.map((d, i) => (
          <li key={i} className={styles.varItem}>
            <div className={styles.varName}>{d.name}</div>
            <p className={styles.varDesc}>{d.description}</p>
            <div className={styles.varAction}>
              <span className={styles.varActionLabel}>Required for</span>
              <span>{d.required_for}</span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
};

const CounterpartiesCard = ({ parties }: { parties: Counterparty[] }) => {
  if (parties.length === 0) return null;
  return (
    <section className={styles.card}>
      <h3 className={styles.h3}>
        Counterparties <span className={styles.count}>({parties.length})</span>
      </h3>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.th}>Name</th>
            <th className={styles.th}>Role</th>
            <th className={styles.th}>Source</th>
          </tr>
        </thead>
        <tbody>
          {parties.map((p, i) => (
            <tr key={i}>
              <td className={styles.td}>{p.name}</td>
              <td className={styles.td}>{p.role}</td>
              <td className={styles.td}>{p.source_upload_id ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
};

export const RunDetailRoute = () => {
  const { projectId, runId } = useParams<{
    projectId: string;
    runId: string;
  }>();
  const { data: run, isLoading, error } = useRun(projectId, runId);
  const { data: resolutions } = useResolutionsList(projectId);

  if (!projectId || !runId) {
    return (
      <section className={styles.card}>
        <p className={styles.err}>
          Run URL is missing the project or run id. Pick a row from{" "}
          <Link to="/operations">Operations</Link>.
        </p>
      </section>
    );
  }

  if (isLoading) {
    return (
      <section className={styles.card}>
        <p className={styles.muted}>Loading run…</p>
      </section>
    );
  }

  if (error || !run) {
    return (
      <section className={styles.card}>
        <p className={styles.err}>
          Could not load run <code>{runId}</code> for project{" "}
          <code>{projectId}</code>.{" "}
          {error instanceof Error ? error.message : null}
        </p>
        <Link to="/operations" className={styles.backLink}>
          ← Back to Operations
        </Link>
      </section>
    );
  }

  const ps = run.decision?.project_state;

  return (
    <div className={styles.page}>
      <div className={styles.headerBar}>
        <Link to="/operations" className={styles.backLink}>
          ← Operations
        </Link>
        <div className={styles.headerMeta}>
          <span>{formatRelative(run.created_at)}</span>
          <span className={styles.dot}>·</span>
          <span>
            <code className={styles.code}>{run.run_id}</code>
          </span>
        </div>
      </div>

      <section className={styles.headerCard}>
        <div className={styles.headerLeft}>
          <div className={styles.projectLabel}>Project</div>
          <h2 className={styles.projectId}>{run.project_id}</h2>
        </div>
        <div className={styles.headerRight}>
          <RouteBadge mode={run.triage?.chosen_mode} />
          <VerdictPill verdict={run.decision?.verdict} />
        </div>
      </section>

      <KnownGapsBanner gaps={run.decision?.known_gaps ?? []} />
      <SummaryCard run={run} />
      <DecisionSection run={run} />
      <OpenVariablesCard
        projectId={run.project_id}
        sourceRunId={run.run_id}
        vars={ps?.open_variables ?? []}
        resolutions={resolutions ?? []}
      />
      <MissingDocumentsCard docs={ps?.missing_documents ?? []} />
      <CounterpartiesCard parties={ps?.counterparties ?? []} />
    </div>
  );
};
